import React, { useState } from 'react';
import { CallStatusCardProps } from '../types';
import ResultsDisplay from './ResultsDisplay';
import { api } from '../services/api';

const CallStatusCard: React.FC<CallStatusCardProps> = ({ call }) => {

  const [isTranscriptExpanded, setIsTranscriptExpanded] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isResultsCollapsed, setIsResultsCollapsed] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showBaseline, setShowBaseline] = useState(false);

  const formatTimestamp = (timestamp: Date) => {
    return new Date(timestamp).toLocaleString();
  };

  const parseTranscript = (transcript: string) => {
    const lines = transcript.split('\n\n').filter(line => line.trim());
    // Skip the first system message and return the rest
    return lines.slice(1).map(line => {
      const colonIndex = line.indexOf(':');
      if (colonIndex === -1) return null;

      const speaker = line.substring(0, colonIndex).trim().toLowerCase();
      const content = line.substring(colonIndex + 1).trim();

      return {
        speaker,
        content,
        isAssistant: speaker.includes('assistant'),
        isUser: speaker.includes('user')
      };
    }).filter(Boolean);
  };

  const handleAIAnalyse = async () => {
    if (!call.transcript || isAnalyzing) return;

    setIsAnalyzing(true);
    try {
      console.log('🔍 Starting AI analysis for call:', call.id);
      await api.analyzeTranscript(call.id, call.transcript);
      console.log('✅ AI analysis completed');
      // The polling will pick up the updated results
    } catch (error) {
      console.error('❌ AI analysis failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processing':
        return '#ffc107';
      case 'completed':
        return '#28a745';
      case 'failed':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'processing':
        return 'Processing';
      case 'completed':
        return 'Complete';
      case 'failed':
        return 'Failed';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="call-card">
      <div className="card-header">
        <div className="call-info">
          <h3>{call.phoneNumber}</h3>
          <span className="timestamp">{formatTimestamp(call.timestamp)}</span>
          {call.propertyName && (
            <span className="property-name">{call.propertyName}</span>
          )}
        </div>
        <div className="header-actions">
          {call.baseline && (
            <button
              className="baseline-btn"
              onClick={() => setShowBaseline(!showBaseline)}
              aria-label={showBaseline ? 'Hide previous data' : 'Show previous data'}
              title={showBaseline ? 'Hide previous data' : 'Show previous data'}
            >
              {showBaseline ? 'Hide Previous Data' : 'Previous Data'}
            </button>
          )}
          <button
            className="collapse-btn"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? 'Expand call details' : 'Collapse call details'}
          >
            {isCollapsed ? 'Expand' : 'Collapse'}
          </button>
          <button
            className="delete-btn"
            onClick={async () => {
              try {
                await api.deleteCall(call.id);
              } catch (e) {
                console.error('Failed to delete call', e);
              }
            }}
            aria-label={'Delete call record'}
            title={'Delete call record'}
          >
            Delete
          </button>
          <div
            className="status-indicator"
            style={{ backgroundColor: getStatusColor(call.status) }}
          >
            {getStatusText(call.status)}
          </div>
        </div>
      </div>

      {!isCollapsed && call.baseline && showBaseline && (
        <div className="baseline-section">
          <div className="baseline-header">Baseline (pre-filled)</div>
          <div className="baseline-content">
            {(call.baseline.halalOptions || call.baseline.alcoholOptions || call.baseline.leisureFacilities) && (
              <div className="baseline-flags">
                {call.baseline.halalOptions && (
                  <div className="flag-group">
                    <span className="flag-title">Halal:</span>
                    <span className={`flag ${call.baseline.halalOptions.all === 'yes' ? 'on' : ''}`}>All</span>
                    <span className={`flag ${call.baseline.halalOptions.some === 'yes' ? 'on' : ''}`}>Some</span>
                    <span className={`flag ${call.baseline.halalOptions.onRequest === 'yes' ? 'on' : ''}`}>On request</span>
                    <span className={`flag ${call.baseline.halalOptions.nearby === 'yes' ? 'on' : ''}`}>Nearby</span>
                  </div>
                )}
                {call.baseline.alcoholOptions && (
                  <div className="flag-group">
                    <span className="flag-title">Alcohol:</span>
                    <span className={`flag ${call.baseline.alcoholOptions.property === 'yes' ? 'on' : ''}`}>Property</span>
                    <span className={`flag ${call.baseline.alcoholOptions.restaurant === 'yes' ? 'on' : ''}`}>Restaurant</span>
                    <span className={`flag ${call.baseline.alcoholOptions.room === 'yes' ? 'on' : ''}`}>Room</span>
                  </div>
                )}
                {call.baseline.leisureFacilities && (
                  <div className="flag-group">
                    <span className="flag-title">Leisure:</span>
                    <span className={`flag ${call.baseline.leisureFacilities.modestSwimwear === 'yes' ? 'on' : ''}`}>Modest</span>
                    <span className={`flag ${call.baseline.leisureFacilities.womenOnlyFacilities === 'yes' ? 'on' : ''}`}>Women-only</span>
                    <span className={`flag ${call.baseline.leisureFacilities.privateHire === 'yes' ? 'on' : ''}`}>Private</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {!isCollapsed && call.transcript && (
        <div className="transcript-block">
          <div className="transcript-header">
            <h4>Call Transcript</h4>
            <div className="transcript-controls">
              <button
                className="transcript-toggle-btn"
                onClick={() => setIsTranscriptExpanded(!isTranscriptExpanded)}
              >
                {isTranscriptExpanded ? 'Collapse' : 'Expand'}
              </button>
              {call.status !== 'completed' && (
                <button
                  className="ai-analyse-btn"
                  onClick={handleAIAnalyse}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing ? 'Analysing...' : '🤖 AI Analyse'}
                </button>
              )}
            </div>
          </div>

          {isTranscriptExpanded && (
            <div className="chat-container">
              {parseTranscript(call.transcript).map((message, index) => (
                <div
                  key={index}
                  className={`chat-bubble ${message!.isAssistant ? 'assistant' : 'user'}`}
                >
                  <div className="message-content">{message!.content}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!isCollapsed && call.status === 'processing' && !call.transcript && (
        <div className="processing-indicator">
          <div className="loader-ring">
            <div className="loader-center"></div>
          </div>
          <div className="processing-copy">
            <div className="line primary">Call in progress...</div>
            <div className="line sub">We’re gathering the transcript for AI analysis</div>
          </div>
        </div>
      )}

      {!isCollapsed && call.status === 'completed' && call.results && (
        <div className="completed-section">
          <div className="results-header">
            <h4>Results</h4>
            <button
              className="results-toggle-btn"
              onClick={() => setIsResultsCollapsed(!isResultsCollapsed)}
            >
              {isResultsCollapsed ? 'Expand' : 'Collapse'}
            </button>
          </div>
          {!isResultsCollapsed && (
            <ResultsDisplay results={call.results} transcript={call.transcript} baseline={call.baseline} />
          )}
        </div>
      )}

      {!isCollapsed && call.status === 'completed' && !call.results && (
        <div className="analysis-pending">
          <p>Analysis completed but results not available.</p>
        </div>
      )}

      {!isCollapsed && call.status === 'failed' && (
        <div className="error-section">
          <h4>Call Failed</h4>
          {call.errorMessage && <p>{call.errorMessage}</p>}
        </div>
      )}

      <style>{`
        .call-card {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          margin-bottom: 1rem;
          overflow: hidden;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid var(--hb-border);
          background: var(--hb-white);
        }

        .call-info h3 {
          margin: 0;
          font-size: 1.1rem;
          color: #333;
        }

        .timestamp {
          display: block;
          font-size: 0.875rem;
          color: #6c757d;
          margin-top: 0.25rem;
        }

        .property-name {
          display: inline-block;
          margin-top: 0.25rem;
          font-size: 0.85rem;
          color: #495057;
          background: #e9ecef;
          padding: 0.15rem 0.4rem;
          border-radius: 4px;
        }

        .status-indicator {
          padding: 0.5rem 1rem;
          border-radius: 20px;
          color: var(--hb-white);
          font-weight: 600;
          font-size: 0.875rem;
        }

        .header-actions {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .collapse-btn {
          background: var(--hb-muted);
          color: var(--hb-white);
          border: none;
          padding: 0.375rem 0.75rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.2s;
        }

        .collapse-btn:hover {
          background: #566574;
        }

        .baseline-btn {
          background: var(--hb-sea);
          color: var(--hb-white);
          border: none;
          padding: 0.375rem 0.75rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.2s;
        }

        .baseline-btn:hover {
          background: var(--hb-sea-dark);
        }

        .delete-btn {
          background: var(--hb-orange);
          color: var(--hb-white);
          border: none;
          padding: 0.375rem 0.75rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.2s;
        }

        .delete-btn:hover {
          background: var(--hb-orange-dark);
        }

        .processing-indicator {
          padding: 1.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          background: #e8fbff;
          border-left: 4px solid var(--hb-sea);
        }

        .loader-ring {
          position: relative;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: conic-gradient(var(--hb-sea) 0deg, #59c7d3 120deg, #b7ecf2 240deg, var(--hb-sea) 360deg);
          animation: spin 1.2s linear infinite;
          display: grid;
          place-items: center;
        }

        .loader-center {
          width: 18px;
          height: 18px;
          background: var(--hb-white);
          border-radius: 50%;
          box-shadow: inset 0 0 0 2px #e8fbff;
        }

        .processing-copy .line.primary { font-weight: 600; color: var(--hb-sea-dark); }
        .processing-copy .line.sub { font-size: 0.85rem; color: #3a7e89; }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-section {
          padding: 1.5rem;
          background: #f8d7da;
          border-left: 4px solid #dc3545;
        }

        .completed-section {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .results-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        .results-header h4 {
          margin: 0;
          font-size: 1rem;
          color: #333;
        }

        .results-toggle-btn {
          background: #6c757d;
          color: white;
          border: none;
          padding: 0.375rem 0.75rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.2s;
        }

        .results-toggle-btn:hover {
          background: #5a6268;
        }

        .baseline-section {
          background: #f8f9fa;
          border-left: 4px solid #6c757d;
          padding: 1rem;
          border-radius: 4px;
          margin: 0 1.5rem 1rem 1.5rem;
        }
        .baseline-header {
          font-weight: 600;
          color: #495057;
          margin-bottom: 0.5rem;
        }
        .baseline-row {
          color: #495057;
          font-size: 0.9rem;
        }
        .baseline-flags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem 1rem;
          margin-top: 0.5rem;
        }
        .flag-group {
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }
        .flag-title {
          color: #6c757d;
          font-size: 0.8rem;
          margin-right: 0.25rem;
        }
        .flag {
          border: 1px solid #dee2e6;
          border-radius: 999px;
          padding: 0.15rem 0.5rem;
          font-size: 0.75rem;
          color: #6c757d;
          background: #fff;
        }
        .flag.on {
          background: #0d6efd;
          color: #fff;
          border-color: #0d6efd;
        }

        .transcript-block {
          background: #f8f9fa;
          border-left: 4px solid #0d6efd;
          padding: 1rem;
          border-radius: 4px;
        }

        .transcript-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .transcript-header h4 {
          margin: 0;
          color: #0d6efd;
          font-size: 1rem;
        }

        .transcript-controls {
          display: flex;
          gap: 0.5rem;
        }

        .chat-container {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          max-height: 400px;
          overflow-y: auto;
        }

        .chat-bubble {
          max-width: 80%;
          padding: 0.75rem 1rem;
          border-radius: 18px;
          position: relative;
          word-wrap: break-word;
        }

        .chat-bubble.assistant {
          background: #007bff;
          color: white;
          align-self: flex-start;
          border-bottom-left-radius: 4px;
        }

        .chat-bubble.user {
          background: #e9ecef;
          color: #212529;
          align-self: flex-end;
          border-bottom-right-radius: 4px;
        }

        .message-content {
          margin: 0;
          line-height: 1.4;
        }

        .transcript-toggle-btn {
          background: #6c757d;
          color: white;
          border: none;
          padding: 0.375rem 0.75rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.2s;
        }

        .transcript-toggle-btn:hover:not(:disabled) {
          background: #5a6268;
        }

        .ai-analyse-btn {
          background: #007bff;
          color: white;
          border: none;
          padding: 0.375rem 0.75rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.2s;
        }

        .ai-analyse-btn:hover:not(:disabled) {
          background: #0056b3;
        }

        .ai-analyse-btn:disabled {
          background: #6c757d;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .analysis-pending {
          background: #fff7eb;
          border-left: 4px solid var(--hb-orange);
          padding: 1rem;
          border-radius: 4px;
          color: #8a4b0f;
          font-size: 0.95rem;
        }

        .error-section h4 {
          margin: 0 0 0.5rem 0;
          color: #721c24;
        }

        .error-section p {
          margin: 0;
          color: #721c24;
        }
      `}</style>
    </div>
  );
};

export default CallStatusCard;



